import { StyleSheet, css } from 'aphrodite';
import { Skeleton } from '../../../web/components-v2/Skeleton';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import { ITimeEntryEvent, TimeEntryAction, useTimeEntryHistoryQuery } from '../../queries/TimeEntry';
import { formatDateTime } from '../days';
import { formatHours } from '../hours';

const ACTION_LABELS: Record<TimeEntryAction, string> = {
	Approved: 'Approved',
	Entered: 'Entered',
	ExceptionOpened: 'Day opened',
	Rejected: 'Sent back',
	Reopened: 'Reopened',
	Submitted: 'Submitted and attested',
};

const describeHours = (event: ITimeEntryEvent) => {
	if (event.action !== 'Entered' && event.action !== 'Submitted') {
		return undefined;
	}
	if (event.fromHours === undefined) {
		return event.toHours === undefined ? undefined : `${formatHours(event.toHours)} hours`;
	}
	return `${formatHours(event.fromHours)} → ${formatHours(event.toHours)} hours`;
};

export function TimeEntryHistory({ entryId, nameFor }: { entryId: string; nameFor: (userId?: string) => string }) {
	const history = useTimeEntryHistoryQuery({ id: entryId });

	if (history.isLoading) {
		return <Skeleton styles={[styles.skeleton]} />;
	}

	const events = [...(history.data ?? [])].sort((a, b) => b.at.localeCompare(a.at) || b.sequence - a.sequence);
	if (!events.length) {
		return <p className={css(styles.empty)}>No history yet.</p>;
	}

	return (
		<ol className={css(styles.list)}>
			{events.map(event => {
				const hours = describeHours(event);
				return (
					<li key={event.id} className={css(styles.event)}>
						<span>
							<span className={css(styles.action)}>{ACTION_LABELS[event.action]}</span> by {nameFor(event.byUserId)}
						</span>
						<span className={css(styles.meta)}>
							{formatDateTime(event.at)}
							{hours ? ` · ${hours}` : ''}
							{event.version > 1 ? ` · version ${event.version}` : ''}
						</span>
						{event.comment ? <span className={css(styles.comment)}>“{event.comment}”</span> : null}
					</li>
				);
			})}
		</ol>
	);
}

const styles = StyleSheet.create({
	action: {
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	comment: {
		color: THEME.colors.text.main,
	},
	empty: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
	},
	event: {
		borderTop: `1px solid ${THEME.colors.border.light}`,
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
		padding: `${SPACE[12]}px 0`,
	},
	list: {
		fontSize: THEME.typography.fontSizes.sm,
		listStyle: 'none',
		margin: 0,
		padding: 0,
	},
	meta: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.xs,
	},
	skeleton: {
		height: 120,
		width: '100%',
	},
});
